import React, { memo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Card,
  CardActionArea,
  Avatar,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import { useConfiguration } from '../hooks/useConfiguration'

/**
 * Manufacturing widget configuration component
 * Allows selection of a device and configuration of display options
 */
const ManufacturingWidgetConfiguration = memo(({ data, setData, t_ }) => {
  const theme = useTheme()
  const { devices, loading, searchTerm, setSearchTerm } = useConfiguration()
  const showPanelFlag = data?.showPanel ?? true

  const handleSelect = (device) => {
    setData({ ...data, device })
  }

  const handleTogglePanel = (e) => {
    setData({ ...data, showPanel: e.target.checked })
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h6' fontWeight='bold' color='primary'>
        {t_('Select Device')}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={showPanelFlag}
            onChange={handleTogglePanel}
            color='primary'
            size='small'
          />
        }
        label={t_('Show details panel')}
        sx={{ alignSelf: 'flex-start' }}
      />

      <TextField
        fullWidth
        size='small'
        placeholder={t_('Search device...')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon color='action' />
            </InputAdornment>
          )
        }}
        sx={{ mb: 1 }}
      />

      <Paper
        variant='outlined'
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          maxHeight: '400px',
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: 2,
          p: 1
        }}
      >
        {loading
          ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
            )
          : devices.length === 0
            ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant='body2'>{t_('No devices found')}</Typography>
              </Box>
              )
            : (
              <Stack spacing={1}>
                {devices.map((device) => {
                  const isSelected = data?.device?.id === device.id
                  return (
                    <DeviceOption
                      key={device.id}
                      device={device}
                      isSelected={isSelected}
                      onSelect={handleSelect}
                      theme={theme}
                    />
                  )
                })}
              </Stack>
              )}
      </Paper>
    </Box>
  )
})

ManufacturingWidgetConfiguration.displayName = 'ManufacturingWidgetConfiguration'

ManufacturingWidgetConfiguration.propTypes = {
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired
}

/**
 * Device option component
 * Represents a single device option in the list
 */
const DeviceOption = memo(({ device, isSelected, onSelect, theme }) => (
  <Card
    elevation={isSelected ? 4 : 1}
    sx={{
      border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}
  >
    <CardActionArea
      onClick={() => onSelect(device)}
      sx={{ p: 1.5 }}
    >
      <Stack direction='row' alignItems='center' spacing={2}>
        <Avatar
          sx={{
            bgcolor: isSelected ? theme.palette.primary.main : theme.palette.action.selected,
            color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary
          }}
        >
          <PrecisionManufacturingIcon fontSize='small' />
        </Avatar>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='subtitle2' fontWeight='bold' color='text.primary'>
            {device.name}
          </Typography>
          {device.code && (
            <Typography variant='caption' color='text.secondary'>
              {device.code}
            </Typography>
          )}
        </Box>

        {isSelected && (
          <CheckCircleIcon color='primary' fontSize='small' />
        )}
      </Stack>
    </CardActionArea>
  </Card>
))

DeviceOption.displayName = 'DeviceOption'

DeviceOption.propTypes = {
  device: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
}

export default ManufacturingWidgetConfiguration
